import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { isSwipeStackTuningEnabled, resolveSwipeThreshold, type SwipeStackTuning } from "../tuning/swipeStackTuning";
import { SwipeTuningControls } from "./swipeStackTuningPanelSections";

interface SwipeStackTuningPanelProps {
  tuning: SwipeStackTuning;
  onChange: (patch: Partial<SwipeStackTuning>) => void;
  onReset: () => void;
}

export function SwipeStackTuningPanel({ tuning, onChange, onReset }: SwipeStackTuningPanelProps) {
  const [open, setOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 390);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isSwipeStackTuningEnabled()) {
      return;
    }
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const effectiveThreshold = resolveSwipeThreshold(tuning, viewportWidth);

  const copyConfig = useCallback(async () => {
    const payload = {
      ...tuning,
      effectiveSwipeThresholdPx: effectiveThreshold,
      viewportWidth,
    };
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [tuning, effectiveThreshold, viewportWidth]);

  if (!isSwipeStackTuningEnabled()) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          "fixed left-4 z-[60] rounded-full bg-pink-600 px-3 py-2 text-xs font-semibold text-white shadow-lg",
          "bottom-[calc(var(--height-bottom-tab-bar)+env(safe-area-inset-bottom,0px)+0.75rem)]"
        )}
        data-testid="swipe-tuning-toggle"
        aria-expanded={open}
        aria-label="Toggle swipe tuning panel"
      >
        Swipe tune
      </button>

      {open ? (
        <div
          className={clsx(
            "fixed inset-x-0 z-[60] flex max-h-[min(70dvh,32rem)] flex-col",
            "border-t border-gray-200 bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.12)]",
            "bottom-[calc(var(--height-bottom-tab-bar)+env(safe-area-inset-bottom,0px))]"
          )}
          data-testid="swipe-tuning-panel"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Swipe stack tuning</h2>
              <p className="text-[11px] text-gray-500">Live — drag sliders while swiping</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copyConfig}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700"
                data-testid="swipe-tuning-copy"
              >
                {copied ? "Copied" : "Copy JSON"}
              </button>
              <button
                type="button"
                onClick={onReset}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700"
                data-testid="swipe-tuning-reset"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800"
                data-testid="swipe-tuning-close"
                aria-label="Close swipe tuning panel"
              >
                Close
              </button>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
            <SwipeTuningControls
              tuning={tuning}
              effectiveThreshold={effectiveThreshold}
              viewportWidth={viewportWidth}
              onChange={onChange}
              onApplyTinderPreset={onReset}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
